/**
 * Wall-clock helpers for a named time zone, shared by the API (reminder sweeps) and the web app
 * (the `/remind` command). Built on Intl so there is no time-zone dependency.
 */
const WEEKDAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const formatters = new Map();
function formatter(timeZone) {
    let f = formatters.get(timeZone);
    if (!f) {
        try {
            f = new Intl.DateTimeFormat('en-US', {
                timeZone,
                hourCycle: 'h23',
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                weekday: 'short',
            });
        }
        catch {
            f = formatter('UTC');
        }
        formatters.set(timeZone, f);
    }
    return f;
}
/** The wall-clock date and time at `at` in `timeZone`. Unknown zones fall back to UTC. */
export function zonedParts(at, timeZone) {
    const parts = {};
    for (const p of formatter(timeZone).formatToParts(at))
        parts[p.type] = p.value;
    return {
        year: Number(parts.year),
        month: Number(parts.month),
        day: Number(parts.day),
        hour: Number(parts.hour) % 24,
        minute: Number(parts.minute),
        weekday: WEEKDAYS[parts.weekday ?? 'Sun'] ?? 0,
    };
}
/** 'YYYY-MM-DD' for `at` in `timeZone`. */
export function zonedDate(at, timeZone) {
    const p = zonedParts(at, timeZone);
    return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}
/**
 * The instant when the wall clock in `timeZone` reads the given date and time. During a daylight
 * saving gap the result lands just after the gap; during an overlap it picks the first occurrence.
 */
export function zonedToUtc(year, month, day, hour, minute, timeZone) {
    const wanted = Date.UTC(year, month - 1, day, hour, minute);
    let guess = wanted;
    for (let i = 0; i < 3; i++) {
        const p = zonedParts(new Date(guess), timeZone);
        const seen = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
        const diff = wanted - seen;
        if (diff === 0)
            break;
        guess += diff;
    }
    return new Date(guess);
}
/** Adds whole days to a 'YYYY-MM-DD' date. */
export function addDaysIso(date, n) {
    const d = new Date(`${date}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
}
/** Minutes since midnight for 'HH:MM'. */
export function minutesOfDay(hhmm) {
    const [h, m] = hhmm.split(':').map(Number);
    return (h ?? 0) * 60 + (m ?? 0);
}
//# sourceMappingURL=time.js.map